import { ErrorMessage } from '@hookform/error-message';
import { clstx, clsx } from '@payaca/utilities/cvaMerge';
import {
  ComponentProps,
  FC,
  FunctionComponent,
  MouseEventHandler,
  PropsWithChildren,
  useContext,
  useId,
} from 'react';
import {
  FieldError,
  get,
  useController,
  useFormContext,
} from 'react-hook-form';
import Button, { ButtonProps } from '../Button/Button';
import { FieldContext, FieldValidationState } from './Context';
import { FieldError as FieldErrorMessages } from './FieldError';
import FieldLegacy from './FieldLegacy';

/**
 * Field wrapper providing label, helper text, and validation error display.
 *
 * Children should be ordered: Label → HelperText → Input → (other controls).
 * Error messages are rendered automatically at the bottom.
 *
 * @example
 *  <Field name="fieldName">
 *    <Field.Label>Field label</Field.Label>
 *    <Field.HelperText>Guidance for the user</Field.HelperText>
 *    <Select options={[{ label: "option 1", value: 1 }]} value={1} />
 *  </Field>
 */

export type FieldProps = {
  id?: string;
  name: string;
  className?: string;
};

const Field: FC<PropsWithChildren<FieldProps>> = (props) => {
  const { id: customId, name, children, className } = props;

  const generatedId = useId();
  const uId = customId || generatedId;

  /**
   * react hook form context
   */
  const {
    formState: { errors },
  } = useFormContext();

  /**
   * build legacy style `FieldValidationState` object
   */
  // eslint-disable-next-line typescript-eslint/no-redundant-type-constituents -- FieldError resolves to error type in some TS contexts
  const fieldError = get(errors, name);
  const validationState: FieldValidationState | undefined =
    fieldError && !Array.isArray(fieldError)
      ? {
          isValid: false,
          validationMessages: [
            fieldError.message ||
              fieldError.root?.message ||
              `Unknown Error: ${fieldError.type}`,
          ],
        }
      : undefined;

  return (
    <FieldContext.Provider
      value={{
        id: uId,
        name,
        validationState,
      }}
    >
      <div className={clsx('typography:prose space-y-2', className)}>
        {children}

        <ErrorMessage
          name={name}
          errors={errors}
          render={({ message }) => {
            return <FieldErrorMessages messages={[message]} />;
          }}
        />
      </div>
    </FieldContext.Provider>
  );
};

const Label: FC<Omit<ComponentProps<'label'>, 'htmlFor'>> = ({
  children,
  className,
  ...rest
}) => {
  const { id } = useContext(FieldContext);

  return (
    <label htmlFor={id} className={clstx('block', className)} {...rest}>
      {children}
    </label>
  );
};

const Helper: FunctionComponent<PropsWithChildren> = ({ children }) => {
  return <p className="supporting-body text-gray-500">{children}</p>;
};

const Optional: FC<{ className?: string }> = ({ className }) => (
  <p className={clstx('supporting-body text-gray-500', className)}>Optional</p>
);

const ClearButton: FC<PropsWithChildren<{ clearValue: any } & ButtonProps>> = (
  props
) => {
  const { clearValue, onClick, children, ...rest } = props;

  const { name } = useContext(FieldContext);

  if (!name) {
    throw new Error('ClearButton must be used within a Field with a name.');
  }

  const { control } = useFormContext();

  const {
    field: { onChange },
  } = useController({
    name,
    control,
  });

  const handleClear: MouseEventHandler<HTMLButtonElement> = (e) => {
    onChange(clearValue);
    onClick?.(e);
  };

  return (
    <Button
      variant="link-inline"
      linkColour="dark"
      onClick={handleClear}
      {...rest}
    >
      {children || 'Clear'}
    </Button>
  );
};

export default Object.assign(Field, {
  Legacy: FieldLegacy,
  Label,
  HelperText: Helper,
  ClearButton,
  Optional,
});
